import { useCallback, useEffect, useState } from 'react';
import type { TicketTypeTemplateOverride } from '../types/ticketTypeTemplate';
import {
  createTicketTypeTemplateOverride,
  deleteTicketTypeTemplateOverride,
  listTicketTypeTemplateOverrides,
  updateTicketTypeTemplateOverride,
} from '../services/ticketTypeTemplatesService';

interface UseCustomerTicketTemplateOverridesResult {
  overrides: TicketTypeTemplateOverride[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (payload: {
    ticketTypeId: string;
    templateLineItems?: unknown[] | null;
    additionalFields?: unknown[] | null;
  }) => Promise<TicketTypeTemplateOverride>;
  update: (
    overrideId: string,
    patch: {
      templateLineItems?: unknown[] | null;
      additionalFields?: unknown[] | null;
    },
  ) => Promise<TicketTypeTemplateOverride>;
  remove: (overrideId: string) => Promise<void>;
}

export function useCustomerTicketTemplateOverrides(
  customerId: string | null | undefined,
): UseCustomerTicketTemplateOverridesResult {
  const [overrides, setOverrides] = useState<TicketTypeTemplateOverride[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!customerId) {
        setOverrides([]);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const items = await listTicketTypeTemplateOverrides(customerId, signal);
        if (signal?.aborted) return;
        setOverrides(items);
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        setError(
          err instanceof Error ? err.message : 'Failed to load overrides',
        );
      } finally {
        if (!signal?.aborted) setIsLoading(false);
      }
    },
    [customerId],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const refresh = useCallback(async () => {
    await load();
  }, [load]);

  const create = useCallback(
    async (payload: {
      ticketTypeId: string;
      templateLineItems?: unknown[] | null;
      additionalFields?: unknown[] | null;
    }) => {
      if (!customerId) throw new Error('No customer selected');
      const created = await createTicketTypeTemplateOverride(
        customerId,
        payload,
      );
      setOverrides((prev) => [...prev, created]);
      return created;
    },
    [customerId],
  );

  const update = useCallback(
    async (
      overrideId: string,
      patch: {
        templateLineItems?: unknown[] | null;
        additionalFields?: unknown[] | null;
      },
    ) => {
      if (!customerId) throw new Error('No customer selected');
      const updated = await updateTicketTypeTemplateOverride(
        customerId,
        overrideId,
        patch,
      );
      setOverrides((prev) =>
        prev.map((o) => (o.id === overrideId ? updated : o)),
      );
      return updated;
    },
    [customerId],
  );

  const remove = useCallback(
    async (overrideId: string) => {
      if (!customerId) throw new Error('No customer selected');
      await deleteTicketTypeTemplateOverride(customerId, overrideId);
      setOverrides((prev) => prev.filter((o) => o.id !== overrideId));
    },
    [customerId],
  );

  return { overrides, isLoading, error, refresh, create, update, remove };
}
